<template>
  <div class="min-h-screen bg-gray-950">
    <SiteHeaderRibbon sticky />
    <SiteNavBar :sticky="false" />

    <!-- Copy confirmation, mirroring the generator's notification. -->
    <ToastStack>
      <Notification
        :show-notification="showCopied"
        :message="copiedMessage"
        :auto-hide-duration="2000"
        @update:show-notification="showCopied = false"
      />
    </ToastStack>

    <!-- Always present in the prerendered document. `hasBrand` is only ever
         false for the tick it takes the client to recover a payload that
         didn't survive the trip (see the useAsyncData note in the script) —
         better an empty frame for that tick than a page built from blanks. -->
    <PageContainer v-if="hasBrand" as="main" class="py-12 lg:py-16">
      <Breadcrumbs :items="breadcrumbs" class="mb-8" @navigate="(item) => trackBreadcrumb(item.id)" />

      <!-- Hero: logo, headline, the one-sentence answer, and the two things a
           visitor can do next — take this brand as JSON, or go to the site. -->
      <div class="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-12">
        <!-- White-ink logos (most of these assets are the dark-mode variant)
             would be invisible on a white tile — see BrandCard. -->
        <div
          v-if="brand.logo"
          class="flex w-full shrink-0 items-center justify-center rounded-2xl px-8 py-10 lg:w-72"
          :class="brand.logoLight ? 'bg-gray-800 ring-1 ring-inset ring-white/10' : 'bg-white'"
        >
          <!-- Fixed box, for the same reason as BrandCard: `w-auto` sizes from
               the width/height attributes until the asset loads, then snaps to
               the logo's real shape. object-contain keeps the rendered logo
               identical while the box stays put. -->
          <img
            :src="brand.logo"
            :alt="`${brand.name} logo`"
            width="240"
            height="64"
            fetchpriority="high"
            decoding="async"
            class="h-14 w-[240px] max-w-full object-contain"
          />
        </div>

        <div class="min-w-0 flex-1">
          <BaseHeading as="h1" size="display">{{ brand.name }} brand colors</BaseHeading>
          <p class="mt-4 text-lg leading-8 text-gray-300">{{ summary }}</p>
          <p v-if="brand.tagline" class="mt-3 text-base leading-7 text-gray-400">{{ brand.tagline }}</p>

          <div class="mt-6 flex flex-wrap items-center gap-x-6 gap-y-4">
            <BaseButton :href="apiUrl('brand_hero', route.path)" @click="trackApi">
              Get JSON for {{ brand.domain }}
            </BaseButton>
            <TextLink :href="`https://${brand.domain}`" rel="noopener nofollow" class="text-sm" @click="trackVisitSite">
              Visit {{ brand.domain }} <span aria-hidden="true">→</span>
            </TextLink>
          </div>
        </div>
      </div>

      <!-- Each block below carries its own rule and top padding (see
           BrandSection), so the stack only needs the gap between them. -->
      <div class="mt-12 space-y-14 lg:space-y-16">
        <!-- Primary content: the brand palette, as swatches and as a table of
             codes (the format people actually copy from a search result). -->
        <BrandSection
          v-if="brand.brandColors.length"
          anchor="brand-colors"
          :title="`${brand.name} brand color palette`"
          :description="`The core colors ${brand.name} uses across its logo and interface. Click any swatch to copy its hex code.`"
        >
          <div class="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <BrandSwatchCard
              v-for="color in brand.brandColors"
              :key="`brand-${color.value}`"
              :color="color"
              @copy="(value) => copyColor(value, 'brand')"
            />
          </div>

          <div class="mt-8 overflow-x-auto rounded-xl border border-gray-800">
            <table class="min-w-full divide-y divide-gray-800 text-left text-sm">
              <caption class="sr-only">{{ brand.name }} brand colors with hex and RGB values</caption>
              <thead class="bg-gray-900/60">
                <tr>
                  <th scope="col" class="px-4 py-3 font-semibold text-white">Color</th>
                  <th scope="col" class="px-4 py-3 font-semibold text-white">Name</th>
                  <th scope="col" class="px-4 py-3 font-semibold text-white">HEX</th>
                  <th scope="col" class="px-4 py-3 font-semibold text-white">RGB</th>
                  <th scope="col" class="px-4 py-3 font-semibold text-white">Role</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-800/70">
                <tr v-for="color in brand.brandColors" :key="`row-${color.value}`" class="text-gray-300">
                  <td class="px-4 py-3">
                    <span class="block h-6 w-10 rounded ring-1 ring-inset ring-white/20" :style="{ backgroundColor: color.value }"></span>
                  </td>
                  <td class="px-4 py-3">{{ color.name }}</td>
                  <td class="px-4 py-3 font-mono text-white">{{ color.hex || color.value }}</td>
                  <td class="px-4 py-3 font-mono">{{ color.rgb || '—' }}</td>
                  <td class="px-4 py-3 text-gray-400">{{ color.type || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </BrandSection>

        <!-- Every mark the brand publishes, each one copyable and saveable on
             its own. The assets are plain <img> tags so they're in the
             prerendered HTML (and in image search); only the two buttons need
             JavaScript, and both go through the proxy — see useBrandLogos. -->
        <BrandSection
          v-if="brand.logos.length"
          anchor="brand-logos"
          :title="`${brand.name} logos`"
          :description="logosDescription"
        >
          <div class="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <BrandLogoCard
              v-for="logo in brand.logos"
              :key="logo.url"
              :logo="logo"
              :brand-name="brand.name"
              @copy="copyLogoAsset"
              @download="downloadLogoAsset"
            />
          </div>
        </BrandSection>

        <!-- The two extracted palettes, behind the same tab switcher the
             generator uses. The screenshot joins the heading in the sticky
             column, so it stays beside the colors that were sampled from it.

             Both panels stay in the document (v-show, not v-if): the color
             codes are what these pages rank for, so hiding a tab must not take
             its content out of the prerendered HTML. -->
        <BrandSection
          v-if="hasExtractedColors"
          :title="`${brand.name} extracted color palette`"
          :description="extractedDescription"
        >
          <template #aside>
            <div v-if="showsScreenshot" class="mt-6">
              <div class="overflow-hidden rounded-xl border border-gray-800 bg-gray-900">
                <img
                  :src="brand.screenshot"
                  :alt="`Screenshot of the ${brand.domain} homepage`"
                  loading="lazy"
                  decoding="async"
                  class="aspect-[16/10] w-full object-cover object-top"
                />
              </div>
              <p class="mt-2 text-xs text-gray-500">
                The {{ brand.domain }} homepage{{ hasScreenshotColors ? ', as captured for this palette' : '' }}.
              </p>
            </div>
          </template>

          <SectionTabs v-if="showTabs" :tabs="paletteTabs" heading-tag="h3" @change="selectTab" />

          <div
            v-if="hasScreenshotColors"
            v-show="!showTabs || currentTab === 'screenshot'"
            class="grid grid-cols-2 gap-4 sm:grid-cols-3"
            :class="showTabs ? 'mt-6' : ''"
          >
            <BrandSwatchCard
              v-for="color in brand.screenshotColors"
              :key="`shot-${color.value}`"
              :color="color"
              @copy="(value) => copyColor(value, 'screenshot')"
            />
          </div>

          <div
            v-if="hasCodeColors"
            v-show="!showTabs || currentTab === 'code'"
            class="space-y-10"
            :class="showTabs ? 'mt-6' : ''"
          >
            <BrandColorSection
              v-for="group in brand.codeColors"
              :key="group.name"
              :title="titleCase(group.name)"
              :colors="group.colors"
              :heading-tag="codeGroupHeading"
              grid-class="grid grid-cols-2 gap-4 sm:grid-cols-3"
              @copy="(value) => copyColor(value, 'code')"
            />
          </div>
        </BrandSection>

        <!-- The brand description: unique, indexable prose per page. -->
        <BrandSection v-if="brand.description" :title="`About ${brand.name}`">
          <p class="text-base leading-7 text-gray-400">{{ brand.description }}</p>
        </BrandSection>

        <!-- Same API panel the generator and the hub page close with. -->
        <div class="border-t border-gray-800 pt-14 lg:pt-16">
          <ApiAccessCard heading-tag="h2" />
        </div>

        <!-- Internal linking: every brand page points at its neighbours. -->
        <section v-if="related.length" class="border-t border-gray-800 pt-14 lg:pt-16">
          <BaseHeading>More brand palettes</BaseHeading>
          <ul role="list" class="mt-4 flex flex-wrap gap-3">
            <li v-for="other in related" :key="other.slug">
              <NuxtLink
                :to="`/brand-colors/${other.slug}`"
                class="flex items-center gap-2 rounded-full border border-gray-800 bg-gray-900/40 py-1.5 pl-1.5 pr-4 text-sm text-gray-300 transition hover:border-gray-700 hover:text-white"
                @click="trackRelated(other)"
              >
                <img
                  v-if="other.favicon"
                  :src="other.favicon"
                  :alt="`${other.name} favicon`"
                  width="24"
                  height="24"
                  loading="lazy"
                  class="h-6 w-6 rounded-full bg-white object-contain p-0.5"
                />
                {{ other.name }} brand colors
              </NuxtLink>
            </li>
          </ul>
        </section>
      </div>
    </PageContainer>

    <SiteFooter />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { trackEvent } from '~/composables/analytics'
import SiteHeaderRibbon from '~/components/organisms/SiteHeaderRibbon.vue'
import SiteNavBar from '~/components/organisms/SiteNavBar.vue'
import SiteFooter from '~/components/organisms/SiteFooter.vue'
import BaseButton from '~/components/atoms/BaseButton.vue'
import BaseHeading from '~/components/atoms/BaseHeading.vue'
import PageContainer from '~/components/atoms/PageContainer.vue'
import TextLink from '~/components/atoms/TextLink.vue'
import Breadcrumbs from '~/components/molecules/Breadcrumbs.vue'
import Notification from '~/components/molecules/Notification.vue'
import ToastStack from '~/components/molecules/ToastStack.vue'
import BrandColorSection from '~/components/organisms/BrandColorSection.vue'
import BrandSection from '~/components/organisms/BrandSection.vue'
import SectionTabs from '~/components/organisms/SectionTabs.vue'
import BrandSwatchCard from '~/components/molecules/BrandSwatchCard.vue'
import BrandLogoCard from '~/components/molecules/BrandLogoCard.vue'
import ApiAccessCard from '~/components/molecules/ApiAccessCard.vue'
import { copyLogo, downloadLogo } from '~/composables/useBrandLogos'
import { SITE_URL, apiUrl } from '~/utils/site'

const route = useRoute()
// Lowercased to match the catalog: slugs are canonically lowercase, and both
// the payload key below and the API lookup are keyed on them. A mixed-case URL
// would otherwise miss the prerendered payload and take the recovery path.
const slug = String(route.params.slug).toLowerCase()

// Prerendered per brand (nitro.prerender.routes is seeded from the same
// catalog), so the whole record is already in the HTML — no request happens in
// the browser and crawlers see the colors without executing any JavaScript.
const { data, error, status } = await useAsyncData(`brand-${slug}`, () => $fetch(`/api/brands/${slug}`))

const brandError = () => {
  const statusCode = error.value?.statusCode ?? 404
  return createError({
    statusCode,
    statusMessage: statusCode === 404
      ? `No brand data for "${slug}".`
      : `Brand data for "${slug}" could not be loaded (${statusCode}).`,
    fatal: true,
  })
}

// `idle` means useAsyncData has not run the request at all — and it is the one
// state we must not turn into a 404.
//
// When hydrating a server-rendered page, useAsyncData assumes the payload
// already holds the data and defers its own fetch to onBeforeMount, so the
// `await` above returns immediately with `data` still empty. On a prerendered
// page that payload is not in the document: Nitro extracts it to
// `<route>/_payload.json` and the client fetches it before hydrating. When that
// one request fails — a flaky connection, a CDN hiccup, a build id that moved
// under a cached document — Nuxt only warns ("Cannot load payload") and carries
// on with nothing. Throwing here turned that into a 404 error page over a fully
// rendered document, for real visitors and for Googlebot, on the pages that
// exist to be indexed (Sentry JAVASCRIPT-VUE-6G). The API request that fixes it
// is already queued for the next tick.
//
// Every other state (prerender, client-side navigation) has genuinely asked the
// API by this point, so an empty answer there really is a 404.
if (status.value !== 'idle' && (error.value || !data.value?.brand)) {
  throw brandError()
}

if (import.meta.client) {
  // The deferred hydration fetch, once it settles. Data arriving is the normal
  // outcome; a request that comes back empty or failing is the point at which
  // the error above becomes true.
  watch(status, (value) => {
    if (value === 'error' || (value === 'success' && !data.value?.brand)) {
      showError(brandError())
    }
  })
}

/**
 * Shape-complete stand-in for the tick where the client has no record yet.
 *
 * Nothing renders from it — the document body is gated on `hasBrand` — but the
 * setup block below reads the record unconditionally to build the title, the
 * meta description and the JSON-LD, so it has to be safe to read.
 */
const EMPTY_BRAND = {
  slug,
  domain: '',
  name: '',
  tagline: '',
  description: '',
  logo: '',
  logoLight: false,
  favicon: '',
  screenshot: '',
  brandColors: [],
  screenshotColors: [],
  codeColors: [],
  logos: [],
  colorCount: 0,
  generatorHref: '/',
}

const brand = computed(() => data.value?.brand ?? EMPTY_BRAND)
const related = computed(() => data.value?.related ?? [])
const hasBrand = computed(() => Boolean(data.value?.brand))

// `id` is what trackBreadcrumb reports for each way back.
const breadcrumbs = computed(() => [
  { label: 'Colorize', to: '/', id: 'generator' },
  { label: 'Brand colors', to: '/brand-colors', id: 'hub' },
  { label: brand.value.name },
])

// Every event on this page carries the same two fields, so a report can be
// grouped by brand without reading it back out of the page path.
const analyticsBrand = computed(() => ({ brand: brand.value.slug, domain: brand.value.domain }))

const titleCase = (value) => value.charAt(0).toUpperCase() + value.slice(1)

// --- Screenshot / code palette tabs -----------------------------------------
// Not every brand in the dataset has both palettes. A switcher with one side
// is just a label, so the tabs only appear when there's genuinely a choice; the
// section itself disappears when there are no extracted colors at all.
const hasScreenshotColors = computed(() => brand.value.screenshotColors.length > 0)
const hasCodeColors = computed(() => brand.value.codeColors.length > 0)
const hasExtractedColors = computed(() => hasScreenshotColors.value || hasCodeColors.value)
const showTabs = computed(() => hasScreenshotColors.value && hasCodeColors.value)
// The screenshot lives in that section's sticky column, so it goes with it.
const showsScreenshot = computed(() => hasExtractedColors.value && Boolean(brand.value.screenshot))

// Screenshot colors lead — they're the ones that look like the site. Left null
// until someone picks a tab, so the default follows the data rather than
// whatever was known when setup ran (on the recovery path above, that is
// nothing).
const activeTab = ref(null)
const currentTab = computed(() => activeTab.value ?? (hasScreenshotColors.value ? 'screenshot' : 'code'))

const codeColorCount = computed(() =>
  brand.value.codeColors.reduce((total, group) => total + group.colors.length, 0),
)

// --- Logos ------------------------------------------------------------------
// SVG marks copy as markup and raster ones copy as an image, so the intro says
// what a visitor gets rather than promising one and handing over the other —
// including when the brand only publishes one of the two.
const logosDescription = computed(() => {
  const { name, logos } = brand.value
  const vector = logos.filter((logo) => logo.format === 'SVG').length
  const marks = logos.length === 1 ? 'The mark' : `The ${logos.length} marks`
  const copies =
    vector === logos.length
      ? 'Copy one to the clipboard as SVG code, or save the original file.'
      : vector
        ? 'Copy one to the clipboard (SVG as code, everything else as an image), or save the original file.'
        : 'Copy one to the clipboard as an image, or save the original file.'
  return `${marks} ${name} publishes, at original quality. ${copies}`
})

// The section intro has to describe whichever palettes actually made it onto
// the page, or it promises colors that aren't there.
const extractedDescription = computed(() => {
  const { name, domain } = brand.value
  if (showTabs.value) {
    return `Every color ${name} puts on screen, taken two ways: sampled from a live screenshot of ${domain}, and read out of the site's own stylesheets. Click any swatch to copy its hex code.`
  }
  if (hasScreenshotColors.value) {
    return `Sampled from a live screenshot of ${domain}, so these are the colors a visitor actually sees, including imagery and gradients that never appear in the stylesheet. Click any swatch to copy its hex code.`
  }
  return `Read out of ${domain}'s own stylesheets and markup, grouped by color family. Click any swatch to copy its hex code.`
})

// With the tabs hidden there's nothing between the section's <h2> and a code
// group's heading, so it moves up a level rather than skipping one.
const codeGroupHeading = computed(() => (showTabs.value ? 'h4' : 'h3'))

// The tab labels double as the headings for each palette (SectionTabs renders
// them as <h3> here), which is why they carry the brand rather than being
// generic. The descriptions moved into the section intro, so these stay short.
const paletteTabs = computed(() => {
  if (!showTabs.value) return []
  return [
    {
      id: 'screenshot',
      name: `${brand.value.name} screenshot colors`,
      subName: 'Sampled from the homepage shot, including imagery and gradients the stylesheet never mentions.',
      count: brand.value.screenshotColors.length,
      current: currentTab.value === 'screenshot',
    },
    {
      id: 'code',
      name: `${brand.value.domain} code colors`,
      subName: "Read from the site's stylesheets and markup, grouped by color family.",
      count: codeColorCount.value,
      current: currentTab.value === 'code',
    },
  ]
})

const selectTab = (tab) => {
  activeTab.value = tab.id
  try {
    trackEvent(`Brand Page Tab (${tab.id})`, analyticsBrand.value)
  } catch {}
}

/** "Dodger Blue (#0070F8), Black (#000000) and White (#FFFFFF)" */
const colorPhrase = (colors) => {
  const parts = colors.map((color) => `${color.name} (${color.hex || color.value})`)
  if (parts.length <= 1) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

// The one-sentence answer, kept identical in the page body and the meta
// description so the snippet matches what a visitor lands on.
const summary = computed(() => {
  const colors = brand.value.brandColors
  if (!colors.length) {
    return `The color palette of ${brand.value.domain}, extracted from its logo, stylesheets and homepage.`
  }
  return `${brand.value.name}'s brand colors are ${colorPhrase(colors)}.`
})

const hexList = computed(() =>
  brand.value.brandColors.slice(0, 4).map((color) => color.hex || color.value).join(', '),
)

// The head values are computed, not plain constants: on the recovery path the
// record lands a tick after setup, and the tab title, the meta description and
// the JSON-LD all have to follow it rather than freeze on the placeholder.
const title = computed(() => `${brand.value.name} Brand Colors - Hex Codes, RGB & Palette`)
const description = computed(() =>
  brand.value.brandColors.length
    ? `${brand.value.name} brand colors: ${hexList.value}. Copy the hex and RGB codes for ${brand.value.domain}, plus its logo, homepage colors and full CSS palette.`
    : `The full color palette of ${brand.value.domain}: hex and RGB codes extracted from its logo, stylesheets and homepage.`,
)

// The URL identifies the page, so it comes from the route rather than the
// record — it is correct even before the data is.
const canonical = `${SITE_URL}/brand-colors/${slug}`
// A dedicated 1200x630 card is generated for every catalog entry by
// scripts/generate-brand-social-images.mjs. It includes the primary/alternate
// marks, every official brand color and Colorize branding, and is committed as
// a static PNG so social crawlers never depend on JavaScript or Worker rendering.
const socialImage = `${SITE_URL}/social/brand-colors/${slug}.png`

// Structured data. The Organization node is what carries the logo and the
// description into search results; the ImageObject nodes make both images
// eligible for image search in their own right.
const jsonLd = computed(() => JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: title.value,
      description: description.value,
      inLanguage: 'en',
      isPartOf: { '@type': 'WebSite', name: 'Colorize', url: SITE_URL },
      about: { '@id': `${canonical}#brand` },
      breadcrumb: { '@id': `${canonical}#breadcrumb` },
      // Only claimed when the screenshot is actually on the page — it renders
      // beside the extracted colors, so a brand with none of those doesn't show
      // it and mustn't declare it either.
      ...(showsScreenshot.value
        ? {
            primaryImageOfPage: {
              '@type': 'ImageObject',
              url: brand.value.screenshot,
              caption: `Screenshot of the ${brand.value.domain} homepage`,
            },
          }
        : {}),
    },
    {
      '@type': 'Organization',
      '@id': `${canonical}#brand`,
      name: brand.value.name,
      url: `https://${brand.value.domain}`,
      description: brand.value.description || brand.value.tagline || undefined,
      slogan: brand.value.tagline || undefined,
      ...(brand.value.logo
        ? {
            logo: {
              '@type': 'ImageObject',
              url: brand.value.logo,
              caption: `${brand.value.name} logo`,
            },
          }
        : {}),
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Colorize', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Brand colors', item: `${SITE_URL}/brand-colors` },
        { '@type': 'ListItem', position: 3, name: `${brand.value.name} brand colors`, item: canonical },
      ],
    },
  ],
  // `<` can't appear raw inside a <script> block without ending it early.
}).replace(/</g, '\\u003c'))

useHead({
  title: () => title.value,
  link: [
    { rel: 'canonical', href: canonical },
    // The logo is above the fold and served from the Prefetch CDN.
    { rel: 'preconnect', href: 'https://curated.prefetch.io', crossorigin: '' },
  ],
  htmlAttrs: { lang: 'en' },
  script: [{ type: 'application/ld+json', innerHTML: () => jsonLd.value }],
})

useSeoMeta({
  description: () => description.value,
  ogTitle: () => title.value,
  ogDescription: () => description.value,
  ogType: 'article',
  ogUrl: canonical,
  ogImage: socialImage,
  ogImageSecureUrl: socialImage,
  ogImageType: 'image/png',
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogImageAlt: () => `${brand.value.name} brand colors`,
  twitterCard: 'summary_large_image',
  twitterTitle: () => title.value,
  twitterDescription: () => description.value,
  twitterImage: socialImage,
  twitterImageAlt: () => `${brand.value.name} logos and brand color palette by Colorize`,
})

// One toast for the whole page. The logo actions each have their own outcome
// to report (SVG code, an image, a fallback URL, a failure), so the message is
// a ref rather than a constant on the component.
const showCopied = ref(false)
const copiedMessage = ref('Color Copied!')

const notify = (message) => {
  copiedMessage.value = message
  showCopied.value = true
}

// Which palette the swatch came from is the whole question these pages answer —
// whether people copy the official brand colors or the ones extracted from the
// site — so it goes in the event name rather than only in a parameter.
const copyColor = (value, source = 'brand') => {
  try {
    navigator.clipboard?.writeText(value)
    trackEvent(`Brand Page Color Copied (${source})`, { ...analyticsBrand.value, color: value, source })
  } catch {
    // Clipboard unavailable (insecure context / permissions) — the value is
    // still on screen, so this is not worth surfacing.
  }
  notify('Color Copied!')
}

const copyLogoAsset = async (logo) => {
  notify(await copyLogo(logo, brand.value.slug))
}

const downloadLogoAsset = async (logo) => {
  notify(await downloadLogo(logo, brand.value.slug))
}

const trackApi = () => {
  try {
    trackEvent('Brand Page API Clicked', analyticsBrand.value)
  } catch {}
}

// Outbound to the brand's own site — the main way off this page that isn't ours.
const trackVisitSite = () => {
  try {
    trackEvent('Brand Page Site Visited', analyticsBrand.value)
  } catch {}
}

// Internal linking is what these pages are built on; this says whether visitors
// actually walk it, and to which neighbour.
const trackRelated = (other) => {
  try {
    trackEvent('Brand Page Related Clicked', { ...analyticsBrand.value, target: other.slug })
  } catch {}
}

const trackBreadcrumb = (destination) => {
  try {
    trackEvent(`Brand Page Breadcrumb (${destination})`, analyticsBrand.value)
  } catch {}
}
</script>
